import React, { useMemo, useState } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CssBaseline,
  Box,
  Button,
  Divider,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Home from "./Home";
import About from "./About";
import Features from "./Features";
import Contact from "./Contact";
import Dashboard from "./DashBoard";
import Footer from "./Footer";
import LanguageSwitcher from "./LanguageSwitcher";
import AIKernelAssistant from "./AIKernelAssistant";
import { useKernel } from "./Shortener/hooks/AIKernel";

const drawerWidth = 280;

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -8 },
};

const pageTransition = {
  type: "tween",
  ease: "easeOut",
  duration: 0.24,
};

const Console = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { agentState } = useKernel();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:899px)");
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? "dark" : "light",
          primary: { main: isDarkMode ? "#d7b879" : "#8b6a35" },
          secondary: { main: "#a8b9d0" },
          background: {
            default: isDarkMode ? "#0b0c0f" : "#f5f3ef",
            paper: isDarkMode ? "#14161b" : "#fffefa",
          },
          text: {
            primary: isDarkMode ? "#f4f0e8" : "#1b1b1a",
            secondary: isDarkMode ? "#a7a8ad" : "#686862",
          },
          divider: isDarkMode ? "rgba(255,255,255,0.09)" : "rgba(28,28,26,0.1)",
        },
        typography: {
          fontFamily: "'Inter', 'Roboto', sans-serif",
          button: { textTransform: "none", fontWeight: 600 },
        },
        shape: { borderRadius: 14 },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: { backgroundImage: "none" },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { borderRadius: 10, textTransform: "none" },
            },
          },
        },
      }),
    [isDarkMode]
  );

  const menuItems = [
    { text: t("home1"), path: "/", key: "home" },
    { text: t("about1"), path: "/about", key: "about" },
    { text: t("features1"), path: "/features", key: "features" },
    { text: t("contact1"), path: "/contact", key: "contact" },
    { text: t("dashboard1"), path: "/dashboard", key: "dashboard" },
  ];

  const handleDrawerToggle = () => setMobileOpen((open) => !open);

  const navigationItems = (mobile = false) =>
    menuItems.map((item) =>
      mobile ? (
        <ListItem key={item.key} disablePadding>
          <ListItemButton
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            onClick={() => setMobileOpen(false)}
            sx={{
              minHeight: 48,
              borderRadius: 2,
              "&.Mui-selected": {
                color: "primary.main",
                bgcolor: "rgba(215,184,121,0.1)",
              },
            }}
          >
            <ListItemText primary={item.text} />
          </ListItemButton>
        </ListItem>
      ) : (
        <Button
          key={item.key}
          component={Link}
          to={item.path}
          color="inherit"
          aria-current={location.pathname === item.path ? "page" : undefined}
          sx={{
            px: 1.5,
            py: 1,
            color: location.pathname === item.path ? "primary.main" : "text.secondary",
            "&:hover": { color: "text.primary", bgcolor: "rgba(255,255,255,0.045)" },
          }}
        >
          {item.text}
        </Button>
      )
    );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="studio-shell" sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <AppBar
          position="sticky"
          elevation={0}
          color="transparent"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: isDarkMode ? "rgba(11,12,15,0.88)" : "rgba(255,254,250,0.92)",
            backdropFilter: "blur(18px)",
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 68, md: 76 }, maxWidth: 1440, width: "100%", mx: "auto", px: { xs: 2, md: 4 } }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="Open navigation"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1.5 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box
              component={Link}
              to="/"
              aria-label="VideoKing Studio home"
              sx={{ display: "flex", alignItems: "center", gap: 1.3, color: "inherit", textDecoration: "none", mr: "auto" }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "11px",
                  display: "grid",
                  placeItems: "center",
                  color: "#11110f",
                  background: "linear-gradient(145deg,#f0d79f,#b58c4d)",
                  boxShadow: "0 5px 20px rgba(190,151,83,0.2)",
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 19 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 750, letterSpacing: "0.04em", lineHeight: 1.2 }}>
                  VIDEOKING
                </Typography>
                <Typography sx={{ fontSize: 9, letterSpacing: "0.22em", color: "text.secondary", lineHeight: 1.5 }}>
                  CREATIVE STUDIO
                </Typography>
              </Box>
            </Box>

            {!isMobile && (
              <Box component="nav" aria-label="Main navigation" sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 2 }}>
                {navigationItems()}
              </Box>
            )}
            {!isMobile && (
              <Chip
                size="small"
                icon={<AutoAwesomeIcon sx={{ fontSize: "15px !important" }} />}
                label="AI Kernel active"
                variant="outlined"
                sx={{
                  color: "text.secondary",
                  borderColor: "divider",
                  "& .MuiChip-icon": { color: "primary.main" },
                }}
              />
            )}
          </Toolbar>
        </AppBar>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              bgcolor: "background.paper",
              borderRight: 1,
              borderColor: "divider",
            },
          }}
        >
          <Box sx={{ px: 2.5, py: 2.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.16em" }}>
              VideoKing Studio
            </Typography>
          </Box>
          <Divider />
          <List sx={{ p: 2, display: "grid", gap: 0.5 }}>{navigationItems(true)}</List>
          <Box sx={{ px: 3, pt: 1 }}>
            <LanguageSwitcher />
          </Box>
        </Drawer>

        <Box component="main" sx={{ flexGrow: 1, width: "100%" }}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
              <Route path="/about" element={<PageWrapper><About /></PageWrapper>} />
              <Route path="/features" element={<PageWrapper><Features /></PageWrapper>} />
              <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} />
              <Route path="/dashboard" element={<PageWrapper><Dashboard /></PageWrapper>} />
            </Routes>
          </AnimatePresence>
        </Box>

        <Footer />
        <AIKernelAssistant />
      </Box>
    </ThemeProvider>
  );
};

const PageWrapper = ({ children }) => (
  <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
  >
    {children}
  </motion.div>
);

export default Console;
